import type { ContextKind, ContextMismatch, ContextProbe, ContextsSignals } from '@fraudeai/schema';
import { defineModule, type ModuleContext } from '../harness.js';
import { attempt, attemptAsync, withTimeout } from '../util/safe.js';
import { contextProbe, CONTEXT_PROBE_SOURCE } from '../probe/contextProbe.js';

/**
 * Сравнение одних и тех же сигналов между исполнительными контекстами.
 *
 * Самый надёжный детектор антидетект-браузеров и наспех сделанных
 * расширений-спуферов. Подмена почти всегда ставится в главный поток страницы:
 * там её видно инструментам разработчика и там она «работает». Воркеры,
 * service worker и свежесозданные iframe при этом остаются нетронутыми —
 * у них отдельный realm, и патч туда не доезжает.
 *
 * Настоящий браузер обязан давать одинаковые ответы во всех контекстах.
 * Любое расхождение в userAgent, числе ядер, таймзоне или рендерере GPU —
 * это не «особенность», это след подмены.
 */

/** Поля, которые сравниваются между контекстами. */
const COMPARED_FIELDS: (keyof ContextProbe)[] = [
  'userAgent',
  'uaDataPlatform',
  'uaDataMobile',
  'language',
  'languages',
  'hardwareConcurrency',
  'deviceMemory',
  'platform',
  'vendor',
  'webdriver',
  'timezone',
  'timezoneOffset',
  'screenWidth',
  'screenHeight',
  'colorDepth',
  'devicePixelRatio',
  'canvasHash',
  'webglRenderer',
  'webglVendor',
  'errorSignature',
  'stackShape',
  'mathSignature',
  // errorSamples намеренно не сравниваются напрямую: их расхождение уже
  // отражено в errorSignature, а сами тексты нужны для объяснения находки.
  // performanceOrigin намеренно не сравнивается: у каждого контекста своя
  // точка отсчёта времени, различие здесь — норма, а не сигнал.
];

const PROBE_TIMEOUT_MS = 2500;

function makeBlobUrl(source: string): string | null {
  return attempt(() => URL.createObjectURL(new Blob([source], { type: 'text/javascript' })));
}

function dedicatedWorkerSource(): string {
  return `const probe = ${CONTEXT_PROBE_SOURCE};
self.onmessage = function () {
  try { self.postMessage({ ok: true, data: probe(self) }); }
  catch (e) { self.postMessage({ ok: false, error: String(e) }); }
};`;
}

function sharedWorkerSource(): string {
  return `const probe = ${CONTEXT_PROBE_SOURCE};
self.onconnect = function (event) {
  const port = event.ports[0];
  port.onmessage = function () {
    try { port.postMessage({ ok: true, data: probe(self) }); }
    catch (e) { port.postMessage({ ok: false, error: String(e) }); }
  };
  port.start();
};`;
}

interface ProbeMessage {
  ok: boolean;
  data?: ContextProbe;
  error?: string;
}

async function probeDedicatedWorker(): Promise<ContextProbe | null> {
  const url = makeBlobUrl(dedicatedWorkerSource());
  if (!url) return null;

  let worker: Worker | null = null;
  try {
    worker = attempt(() => new Worker(url));
    if (!worker) return null;
    const w = worker;

    const result = await withTimeout(
      new Promise<ProbeMessage>((resolve, reject) => {
        w.onmessage = (event: MessageEvent<ProbeMessage>) => resolve(event.data);
        w.onerror = (event) => reject(new Error(String(event.message ?? 'worker error')));
        w.postMessage('probe');
      }),
      PROBE_TIMEOUT_MS,
      'worker',
    );
    return result.ok && result.data ? result.data : null;
  } catch {
    return null;
  } finally {
    attempt(() => worker?.terminate());
    attempt(() => URL.revokeObjectURL(url));
  }
}

async function probeSharedWorker(): Promise<ContextProbe | null> {
  if (typeof SharedWorker === 'undefined') return null;
  const url = makeBlobUrl(sharedWorkerSource());
  if (!url) return null;

  try {
    const worker = attempt(() => new SharedWorker(url));
    if (!worker) return null;

    const result = await withTimeout(
      new Promise<ProbeMessage>((resolve, reject) => {
        worker.port.onmessage = (event: MessageEvent<ProbeMessage>) => resolve(event.data);
        worker.onerror = (event) => reject(new Error(String(event.message ?? 'shared worker error')));
        worker.port.start();
        worker.port.postMessage('probe');
      }),
      PROBE_TIMEOUT_MS,
      'sharedWorker',
    );
    return result.ok && result.data ? result.data : null;
  } catch {
    return null;
  } finally {
    attempt(() => URL.revokeObjectURL(url));
  }
}

/**
 * Service worker требует настоящий same-origin файл — blob-URL не подойдёт.
 * Регистрация обязательно снимается в finally: оставлять на сайте пользователя
 * посторонний service worker ради одной проверки недопустимо.
 */
async function probeServiceWorker(scriptUrl: string): Promise<ContextProbe | null> {
  if (!('serviceWorker' in navigator)) return null;

  let registration: ServiceWorkerRegistration | null = null;
  try {
    registration = await withTimeout(
      navigator.serviceWorker.register(scriptUrl, { scope: scriptUrl.replace(/[^/]*$/, '') }),
      PROBE_TIMEOUT_MS,
      'serviceWorker.register',
    );

    const active = await withTimeout(
      new Promise<ServiceWorker>((resolve, reject) => {
        const existing = registration?.active;
        if (existing) return resolve(existing);
        const installing = registration?.installing ?? registration?.waiting;
        if (!installing) return reject(new Error('нет устанавливаемого service worker'));
        installing.addEventListener('statechange', () => {
          if (installing.state === 'activated') resolve(installing);
        });
      }),
      PROBE_TIMEOUT_MS,
      'serviceWorker.activate',
    );

    const result = await withTimeout(
      new Promise<ProbeMessage>((resolve, reject) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = (event: MessageEvent<ProbeMessage>) => resolve(event.data);
        channel.port1.onmessageerror = () => reject(new Error('ошибка канала'));
        active.postMessage({ type: 'fraudeai:probe' }, [channel.port2]);
      }),
      PROBE_TIMEOUT_MS,
      'serviceWorker.probe',
    );

    return result.ok && result.data ? result.data : null;
  } catch {
    return null;
  } finally {
    await attemptAsync(() => registration?.unregister());
  }
}

async function probeIframe(kind: 'iframe' | 'iframeSandboxed' | 'iframeBlank'): Promise<ContextProbe | null> {
  if (typeof document === 'undefined') return null;

  let frame: HTMLIFrameElement | null = null;
  try {
    frame = document.createElement('iframe');
    frame.style.cssText =
      'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;border:0;visibility:hidden;';

    if (kind === 'iframeSandboxed') {
      // allow-same-origin оставляем: без него contentWindow станет
      // кросс-доменным и прочитать из него ничего не выйдет. Смысл в другом —
      // часть расширений и патчей не доезжает до sandbox-фреймов.
      frame.setAttribute('sandbox', 'allow-same-origin');
      frame.src = 'about:blank';
    } else if (kind === 'iframeBlank') {
      frame.setAttribute('srcdoc', '<!doctype html><meta charset="utf-8"><title>p</title>');
    } else {
      frame.src = 'about:blank';
    }

    document.body.appendChild(frame);
    const el = frame;

    await withTimeout(
      new Promise<void>((resolve) => {
        if (el.contentWindow?.document.readyState === 'complete') return resolve();
        el.addEventListener('load', () => resolve(), { once: true });
      }),
      1500,
      `${kind}.load`,
    ).catch(() => undefined);

    const win = el.contentWindow;
    if (!win) return null;

    // Проба исполняется в нашем realm, но читает объекты из realm фрейма —
    // ровно то, что нужно, чтобы увидеть непропатченные значения.
    return attempt(() => contextProbe(win));
  } catch {
    return null;
  } finally {
    attempt(() => frame?.remove());
  }
}

function compare(
  base: ContextProbe,
  baseKind: ContextKind,
  other: ContextProbe,
  otherKind: ContextKind,
): ContextMismatch[] {
  const out: ContextMismatch[] = [];

  for (const field of COMPARED_FIELDS) {
    const a = base[field];
    const b = other[field];
    // Недоступность сигнала в одном из контекстов — норма (screen в воркере),
    // расхождением считается только различие двух реально полученных значений.
    if (a === null || b === null) continue;

    let equal: boolean;
    if (Array.isArray(a) && Array.isArray(b)) {
      // Для languages сравниваем только основной язык: полный список в
      // Shared Worker и Service Worker браузер отдаёт урезанным (['ru']
      // вместо ['ru', 'ru-RU']) — это штатное поведение спецификации,
      // а не подмена. А вот разный основной язык объяснить уже нечем.
      equal = field === 'languages' ? a[0] === b[0] : a.join(' ') === b.join(' ');
    } else {
      equal = a === b;
    }

    if (!equal) {
      out.push({ field, contextA: baseKind, contextB: otherKind, valueA: a, valueB: b });
    }
  }

  return out;
}

export const contextsModule = defineModule({
  id: 'contexts',
  phase: 1,
  timeoutMs: 15000,
  async run(ctx: ModuleContext): Promise<ContextsSignals> {
    const main = attempt(() => contextProbe(globalThis));

    const [worker, shared, iframe, iframeSandboxed, iframeBlank, serviceWorker] = await Promise.all([
      probeDedicatedWorker(),
      probeSharedWorker(),
      probeIframe('iframe'),
      probeIframe('iframeSandboxed'),
      probeIframe('iframeBlank'),
      ctx.options.serviceWorkerUrl
        ? probeServiceWorker(ctx.options.serviceWorkerUrl)
        : Promise.resolve(null),
    ]);

    const probes: Partial<Record<ContextKind, ContextProbe | null>> = {
      main,
      worker,
      sharedWorker: shared,
      serviceWorker,
      iframe,
      iframeSandboxed,
      iframeBlank,
    };

    const unavailable = (Object.keys(probes) as ContextKind[]).filter((kind) => !probes[kind]);

    const mismatches: ContextMismatch[] = [];
    if (main) {
      for (const kind of Object.keys(probes) as ContextKind[]) {
        if (kind === 'main') continue;
        const other = probes[kind];
        if (other) mismatches.push(...compare(main, 'main', other, kind));
      }
    }

    return { probes, unavailable, mismatches, mismatchCount: mismatches.length };
  },
});
